import { buildPartsUrl, resolvePartsSearch, normalizeSearchQuery } from './parts-navigation-core.js';

const dataElement = document.getElementById('PartsNavigationData');
let navigation;
try { navigation = JSON.parse(dataElement?.textContent || '{}'); } catch { navigation = {}; }
const categories = navigation.categories || [];
const models = navigation.models || [];
const origin = window.location.origin;

function navigateParts(collectionUrl, tag) {
  const destination = buildPartsUrl(collectionUrl, tag, window.location.href, origin);
  if (destination) window.location.assign(destination);
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[data-part-link]');
  if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
  const destination = buildPartsUrl(link.dataset.collectionUrl, link.dataset.partTag || '', window.location.href, origin);
  if (!destination) return;
  event.preventDefault();
  window.location.assign(destination);
});

function setupFinders(root = document) {
  root.querySelectorAll('[data-parts-finder]').forEach((form) => {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';
    form.hidden = false;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      navigateParts(form.elements.model.value, form.elements.part.value);
    });
  });
}

const cache = new Map();
function setupSearch(root = document) {
  root.querySelectorAll('[data-store-search]').forEach((container, index) => {
    if (container.dataset.ready) return;
    const form = container.querySelector('form') || container.closest('form');
    const input = container.querySelector('input[name="q"]');
    const popup = container.querySelector('[data-search-suggestions]');
    if (!form || !input || !popup) return;
    container.dataset.ready = 'true';
    const popupId = popup.id || `PartsSearch-${container.id || index}`;
    popup.id = popupId;
    popup.setAttribute('role', 'listbox');
    popup.setAttribute('aria-label', 'Search suggestions');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', popupId);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocomplete', 'off');
    let timer, controller, version = 0, selected = -1;

    const close = () => {
      popup.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      selected = -1;
    };
    const addLink = (label, url, kind, imageUrl) => {
      let destination;
      try { destination = new URL(url, origin); } catch { return; }
      if (destination.origin !== origin) return;
      const link = document.createElement('a');
      link.href = destination.pathname + destination.search;
      link.className = 'search-suggestion';
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.id = `${popupId}-option-${popup.querySelectorAll('[role="option"]').length}`;
      link.tabIndex = -1;
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        img.width = 42; img.height = 42;
        link.append(img);
      }
      const text = document.createElement('span');
      const title = document.createElement('strong'); title.textContent = label;
      const subtitle = document.createElement('small'); subtitle.textContent = kind;
      text.append(title, subtitle); link.append(text); popup.append(link);
    };
    const render = (query, products = [], pending = false) => {
      popup.replaceChildren(); selected = -1;
      input.removeAttribute('aria-activedescendant');
      const normalized = query.trim().toLowerCase();
      const direct = resolvePartsSearch(query, categories, models, navigation.allUrl);
      if (direct) addLink(`Browse ${query.trim()}`, direct, 'Browse matching parts');
      else {
        categories.filter((category) => [category.label, ...category.aliases].some((alias) => alias.toLowerCase().includes(normalized))).slice(0, 3)
          .forEach((category) => addLink(category.label, `${navigation.allUrl}/${category.tag}`, 'Shop by part'));
      }
      products.slice(0, 5).forEach((product) => addLink(product.title, product.url, 'View product', product.image || product.featured_image?.url));
      const queryUrl = new URL(form.action, origin);
      queryUrl.searchParams.set('q', normalizeSearchQuery(query));
      queryUrl.searchParams.set('type', 'product');
      addLink(`Search for “${query.trim()}”`, queryUrl.href, pending ? 'Finding products…' : 'See all results');
      popup.hidden = false; input.setAttribute('aria-expanded', 'true');
    };
    const suggest = async () => {
      const query = input.value.trim(); const requestVersion = ++version;
      controller?.abort();
      if (query.length < 2) { close(); return; }
      const normalized = normalizeSearchQuery(query);
      if (cache.has(normalized)) { render(query, cache.get(normalized)); return; }
      render(query, [], true);
      controller = new AbortController();
      try {
        const url = new URL(navigation.suggestUrl || '/search/suggest.json', origin);
        url.searchParams.set('q', normalized);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '5');
        url.searchParams.set('resources[options][fields]', 'title,variants.sku,variants.title');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        const products = data.resources?.results?.products || [];
        cache.set(normalized, products);
        if (cache.size > 30) cache.delete(cache.keys().next().value);
        if (requestVersion === version && input.value.trim() === query && container.contains(document.activeElement)) render(query, products);
      } catch (error) {
        if (error.name !== 'AbortError' && requestVersion === version) render(query);
      }
    };
    input.addEventListener('input', (event) => {
      clearTimeout(timer); controller?.abort(); version++;
      close();
      if (!event.isComposing) timer = setTimeout(suggest, 180);
    });
    input.addEventListener('compositionend', () => { clearTimeout(timer); timer = setTimeout(suggest, 180); });
    input.addEventListener('focus', () => { if (input.value.trim().length >= 2) suggest(); });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { clearTimeout(timer); controller?.abort(); version++; close(); return; }
      if (popup.hidden) return;
      const options = [...popup.querySelectorAll('[role="option"]')];
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        selected = selected < 0 ? (step === 1 ? 0 : options.length - 1) : (selected + step + options.length) % options.length;
        options.forEach((option, i) => option.setAttribute('aria-selected', String(i === selected)));
        input.setAttribute('aria-activedescendant', options[selected].id);
        options[selected].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && selected >= 0) {
        event.preventDefault(); options[selected].click();
      }
    });
    container.addEventListener('focusout', (event) => { if (!container.contains(event.relatedTarget)) { controller?.abort(); version++; close(); } });
    form.addEventListener('submit', (event) => {
      if (event.submitter?.closest('.catalogue-sort')) return;
      const destination = resolvePartsSearch(input.value, categories, models, navigation.allUrl);
      if (destination) { event.preventDefault(); window.location.assign(destination); }
      else input.value = normalizeSearchQuery(input.value);
    });
  });
}

setupFinders(); setupSearch();
document.addEventListener('shopify:section:load', (event) => { setupFinders(event.target); setupSearch(event.target); });
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const menu = document.activeElement.closest('.parts-model-menu[open], .mobile-menu-group[open]');
  if (menu) { menu.open = false; menu.querySelector('summary').focus(); }
});
